import {
  Flex,
  FormControl,
  FormLabel,
  Input,
  Link,
  ListItem,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
  UnorderedList,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { validateMaxFile, validateTypeFile, validationSizeFile } from '../../../utils/validationForm';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { IResourceExemptions, useRequestNewResource } from '../../../services/hooks/Resources/useGetRequestNewResource';
import { useCreateResource } from '../../../services/hooks/Resources/useCreateResource';
import MainCard from '../../../components/MainCard/Index';
import MessageError from '../../../components/MessageError';
import { TextL, TextS } from '../../../components/Text/Index';
import { DefaultButton } from '../../../components/ButtonDefault/Index';
import { IoLink } from 'react-icons/io5';
import { useCandidatefSubjects } from '../../../services/hooks/Resources/useCandidateSubjects';
import {
  IExaminationBoardResponse,
  useGetExaminationBoard,
} from '../../../services/hooks/ExaminationBoard/useGetExaminationBoard';
import { useAuditLogger } from '../../../services/hooks/Audit';
import { useGetResourceTitleList } from '../../../services/hooks/Titles/useNewResourceTitle';

const NewResource: React.FC = () => {
  const [selectedName, setSelectedName] = useState('');

  const navigate = useNavigate();
  // const [isOpen, setIsOpen] = useState(false);
  const toast = useToast();
  const resource_admin_id = useLocation().pathname.split('/')[4] as string;
  const { data } = useRequestNewResource(resource_admin_id);
  const subjectData = useCandidatefSubjects({
    selective_process_id: data?.selective_process_id || '',
    registration_id: data?.registration?.id || '',
  });

  const titlesList = useGetResourceTitleList({
    remove_pagination: true,
    registrationId: data?.registration?.id.toString(),
  });

  const examinationBoardList = useGetExaminationBoard<IExaminationBoardResponse[]>({
    remove_pagination: true,
    selective_process: data?.selective_process_id?.toString(),
    status: 'published',
    show_members: true,
    resource_period_id: resource_admin_id,
  });

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = event.target.value;
    const selectedItem = data?.exemptions?.find((item) => item?.id === selectedId);
    setSelectedName(selectedItem ? selectedItem.type : '');
  };
  const schema = Yup.object().shape({
    exemption: Yup.string().when({
      is: () => data?.exemptions,
      then: (schema) =>
        schema
          .test('Allow send exemptions', '', () => {
            if (data?.exemptions.length === 0) {
              toast({
                title: 'Atenção',
                description: 'Você atingiu o limite maximo de recursos',
                status: 'info',
                duration: 4000,
                isClosable: true,
              });
              return true;
            }
            return true;
          })
          .required('Campo obrigatorio'),
    }),
    attachmentFile: Yup.mixed()
      .test('invalidExtension', 'Verifique o nome do arquivo enviado', (file) => {
        const listFile = file as FileList;
        let hasError = true;

        for (let index = 0; index < listFile?.length; index++) {
          const hasDot = listFile[index].name.split('.');
          if (hasDot.length > 2) hasError = false;
        }

        return hasError;
      })
      .test('validationSizeFile', `Tamanho máximo para arquivos é de ${data?.size_attached}MB`, (file) => {
        const listFile = file as FileList;

        if (!data?.resource.has_attachment) return true;

        if (listFile?.length > 0) {
          const response = validationSizeFile(listFile, data?.size_attached);
          // if (!response) {
          //   setIsOpen(true);
          // }
          return response;
        }

        return true;
      })
      .test('validationMaxFile', `Quantidade máxima de arquivos é de ${data?.max_attached} arquivos`, (file) => {
        const listFile = file as FileList;
        if (!data?.resource.has_attachment) return true;

        if (listFile?.length > 0) {
          return validateMaxFile(listFile, data?.max_attached);
        }
        return true;
      })
      .test('validationTypeFile', 'Formato de arquivo não suportado', (file) => {
        const listFile = file as FileList;
        if (!data?.resource.has_attachment) return true;
        if (listFile?.length > 0) {
          return validateTypeFile(listFile);
        }
        return true;
      })
      .test('required', 'Envio de arquivo obrigatório', (file) => {
        if (!data?.resource.has_attachment) return true;
        const listFile = file as FileList;

        if (data?.is_required_attachment) {
          if (listFile.length === 0) {
            return false;
          }
        }

        return true;
      }),

    detail: Yup.string().when({
      is: () => data?.resource?.has_observation,
      then: (schema) => schema.required('Campo obrigatório'),
    }),
    nis: Yup.string().when({
      is: () => verifyTypeExemption()?.type.toLowerCase().includes('cadúnico'),
      then: (schema) => schema.required('Campo obrigatório').length(11, 'Campo NIS deve conter 11 digitos'),
    }),
    redome_code: Yup.string().when({
      is: () => verifyTypeExemption()?.type.toLowerCase().includes('medula'),
      then: (schema) => schema.required('Campo obrigatório'),
    }),
    questionNumber: Yup.string().when({
      is: () => data?.resource?.has_question,
      then: (schema) =>
        schema
          .required('Campo obrigatório')
          .test('verifyQuestion', 'Questão não pertence a prova informada', (question) => {
            if (data?.resource?.has_question) {
              if (question) {
                const subject = subjectData.data?.find((s) => s.id === watch('proof_subject'));
                // Se não há questions, não validar
                if (!subject?.questions || subject.questions.length === 0) {
                  return true; // Deixa passar para que outra validação ou mensagem de erro apareça
                }
                if (subject?.questions?.includes(question)) {
                  return true;
                } else {
                  return false;
                }
              }
            }

            return true;
          }),
    }),
    proof_subject: Yup.string().when({
      is: () => data?.resource?.has_question,
      then: (schema) => schema.required('Campo obrigatório'),
    }),
    title_category: Yup.string().when({
      is: () => data?.against_titles,
      then: (schema) => schema.required('Campo obrigatório'),
    }),
    title_item: Yup.string().when({
      is: () => data?.against_titles,
      then: (schema) => schema.required('Campo obrigatório'),
    }),
    examination_board_id: Yup.string().when({
      is: () => data?.against_examination_board,
      then: (schema) => schema.required('Campo obrigatório'),
    }),
    examination_board_member_id: Yup.string(),
  });

  const { register, handleSubmit, formState, setValue, watch } = useForm({
    resolver: yupResolver(schema),
    values: {
      exemption: data?.exemptions?.length === 1 ? data?.exemptions[0]?.id : '',
    },
  });

  const { mutateAsync, isPending } = useCreateResource(
    data?.resource?.id,
    data?.vacancy?.id,
    data?.resource_period_id,
    data?.selective_process_id,
  );

  const { logWithBuilder } = useAuditLogger();

  const newResource = (formData: FieldValues) => {
    mutateAsync(formData)
      .then(async (response) => {
        toast({
          title: 'Sucesso',
          description: 'Seu recurso foi enviado com sucesso',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });

        // LOG DE AUDITORIA
        await logWithBuilder((builder) =>
          builder
            .action('resource_created')
            .level('info')
            .category('resource')
            .resourceType('resource')
            .resourceId(response?.data?.id)
            .selectiveProcessId(data?.selective_process_id || '')
            .message(
              `Candidato enviou um recurso. Justificativa: "${response?.data?.observation}" | Tipo de recurso: ${response?.data?.resource_type?.name} | Anexos: ${response?.data?.attachments?.resource_file?.length || '0'} `,
            ),
        );

        navigate(-1);
      })
      .catch((error) => {
        toast({
          title: 'Atenção',
          description: error?.response?.data?.message || 'Erro ao enviar o recurso, tente novamente',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
      });
  };

  const clear = () => {
    setValue('attachmentFile', '');
  };

  useEffect(() => {
    if (!selectedName.includes('CADÚNICO')) {
      setValue('nis', '');
    }
  }, [selectedName]);

  const verifyTypeExemption = (): IResourceExemptions | undefined => {
    let response = undefined;

    if (data?.exemptions) {
      data?.exemptions
        .filter((exemption) => watch('exemption') === exemption.id)
        .map((ex) => {
          response = ex;
        });
    }

    return response;
  };

  const ex = verifyTypeExemption();

  const selectedTitle = titlesList?.data?.find((item) => item.id === watch('title_category'));

  return (
    <MainCard height="100%" title="ÁREA DO CANDIDATO" subtitle="- NOVO RECURSO">
      <Stack as="form" onSubmit={handleSubmit(newResource)} px={['10px', '10px', '10px', '50px', '50px']} gap={10}>
        <Stack>
          <TextL fontWeight="bold">{data?.selective_process_name}</TextL>
          <Text>
            <strong>Recurso: </strong> {data?.period_identification}
          </Text>
          <Text>
            <strong>Período: </strong> {data?.resource_period}
          </Text>
          <Text>
            <strong>Inscrição: </strong>
            {data?.registration?.registration_code}
          </Text>
          <Text>
            <strong>Nome: </strong>
            {data?.registration?.candidate_name}
          </Text>
          {data?.vacancy?.name && (
            <Text>
              <strong>Vaga: </strong> {data?.vacancy?.name}
            </Text>
          )}
        </Stack>

        {data?.against_titles && (
          <Stack w={['100%', '100%', '100%', '50%', '50%']}>
            <FormControl display="flex" flexDir="column">
              <Text fontWeight="bold" mr={2}>
                Selecione a categoria do título:
              </Text>
              <Select {...register('title_category', { onChange: handleSelectChange })}>
                <option value={''} hidden>
                  Selecione
                </option>
                {titlesList?.data?.length === 0 && <option value={''}>Sem titulos encontrados</option>}
                {titlesList?.data?.map((item) => (
                  <option key={item?.id} value={item?.id}>
                    {item?.name?.toUpperCase()}
                  </option>
                ))}
              </Select>
              <MessageError>{formState.errors.title_category?.message}</MessageError>
            </FormControl>

            {selectedTitle && (
              <FormControl display="flex" flexDir="column">
                <Text fontWeight="bold" mr={2}>
                  Selecione o critério:
                </Text>
                <Select
                  {...register('title_item', { onChange: handleSelectChange })}
                  isDisabled={!watch('title_category') || !selectedTitle?.items || selectedTitle?.items.length === 0}
                >
                  <option value={''} hidden>
                    Selecione
                  </option>
                  {selectedTitle?.items?.length === 0 && <option value={''}>Sem titulos encontrados</option>}
                  {selectedTitle?.items?.map((item) => (
                    <option key={item?.id} value={item?.id}>
                      {item?.name?.toUpperCase()}
                    </option>
                  ))}
                </Select>
                <MessageError>{formState.errors.title_item?.message}</MessageError>
              </FormControl>
            )}
          </Stack>
        )}

        {data?.against_examination_board && (
          <>
            <Stack w={['100%', '100%', '100%', '50%', '50%']}>
              <FormControl display="flex" flexDir="column">
                <Text fontWeight="bold" mr={2}>
                  Selecione a banca para recurso:
                </Text>
                <Select {...register('examination_board_id')}>
                  <option value={''} hidden>
                    Selecione
                  </option>
                  {examinationBoardList?.data?.length === 0 && <option value={''}>Sem bancas encontrads</option>}
                  {examinationBoardList?.data?.map((item) => (
                    <option key={item?.id} value={item?.id}>
                      {`${item?.name}`}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <MessageError>{formState.errors.examination_board_id?.message}</MessageError>
              {/* {selectedExaminationBoard && (
                <>
                  <FormControl display="flex" flexDir="column">
                    <Text fontWeight="bold" mr={2}>
                      Selecione o membro da banca:
                    </Text>
                    <Select {...register('examination_board_member_id')}>
                      <option value={''}>Selecione</option>
                      {selectedExaminationBoard?.members
                        ?.filter((item) => {
                          const role = item?.role_display?.toLowerCase() || '';
                          return role.includes('presidente') || role.includes('efetivo');
                        })
                        ?.map((item) => (
                          <option key={item?.id} value={item?.id}>
                            {`${item?.colaborator_name || item?.colaborator_data?.name || 'Nome não disponível'} - ${item?.role_display?.toUpperCase() || ''}`}
                          </option>
                        ))}
                    </Select>
                  </FormControl>
                  <MessageError>{formState.errors.examination_board_member_id?.message}</MessageError>
                </>
              )} */}
            </Stack>
          </>
        )}

        {data?.resource?.has_question && (
          <Stack w={['100%', '100%', '100%', '50%', '50%']} spacing={4}>
            <FormControl display="flex" flexDir="column" alignItems="flex-start">
              <Text fontWeight="bold" mb={2}>
                Selecione a prova:
              </Text>
              <Select {...register('proof_subject', { onChange: handleSelectChange })} w="100%">
                <option value={''} hidden>
                  Selecione a prova
                </option>
                {subjectData?.data?.map((subject) => (
                  <option key={subject?.id} value={subject?.id}>
                    {subject?.name}
                  </option>
                ))}
              </Select>
              <MessageError>{formState?.errors?.proof_subject?.message}</MessageError>
            </FormControl>

            <FormControl display="flex" flexDir="column" alignItems="flex-start">
              <Text fontWeight="bold" mb={2}>
                Informe o número da questão:
              </Text>

              <Select {...register('questionNumber')} w="100%" disabled={!watch('proof_subject')}>
                <option value={''}>{!watch('proof_subject') ? 'Primeiro selecione a prova...' : 'Selecione...'}</option>
                {subjectData?.data
                  ?.filter((e) => e.id === watch('proof_subject'))
                  .map((subject) => {
                    // Se não tem questions, mostrar mensagem
                    if (!subject.questions || subject.questions.length === 0) {
                      return (
                        <option key="no-questions" value="" disabled>
                          Nenhuma questão disponível para esta prova
                        </option>
                      );
                    }
                    return subject.questions.map((q, index) => (
                      <option key={index} value={q}>
                        {q}
                      </option>
                    ));
                  })}
              </Select>
              {watch('proof_subject') &&
                subjectData?.data?.find((s) => s.id === watch('proof_subject'))?.questions?.length === 0 && (
                  <Text fontSize="xs" color="orange.500" mt={1}>
                    ⚠️ Não há questões cadastradas para esta prova. Contate o administrador.
                  </Text>
                )}
              {/* <Input {...register('questionNumber')} type="number" w="100%" /> */}
              <MessageError>{formState.errors.questionNumber?.message}</MessageError>
            </FormControl>
          </Stack>
        )}

        {data?.exemptions && data?.exemptions.length > 1 && (
          <>
            <Stack w={['100%', '100%', '100%', '50%', '50%']}>
              <FormControl display="flex" flexDir="row" alignItems="center">
                <Text fontWeight="bold" mr={2}>
                  Selecione o tipo de isenção:
                </Text>
                <Select {...register('exemption', { onChange: handleSelectChange })}>
                  <option value={''} hidden>
                    Selecione
                  </option>
                  {data?.exemptions?.length === 0 && <option value={''}>Sem pedidos de isenção encontrados</option>}
                  {data?.exemptions?.map((item) => (
                    <option key={item?.id} value={item?.id}>
                      {item?.type}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <MessageError>{formState.errors.exemption?.message}</MessageError>
            </Stack>
          </>
        )}

        {watch('exemption') && ex && (
          <Stack>
            <TextS fontWeight="bold">{ex?.type}</TextS>

            <Text>
              <strong>Resposta à solicitação de isenção:</strong>
              <Text as="span" dangerouslySetInnerHTML={{ __html: ex?.justification }} />
            </Text>

            {ex?.attachements && ex.attachements?.length > 0 && (
              <Stack>
                <Text fontWeight="bold">Anexos enviados ao solicitar isenção</Text>
                <UnorderedList styleType={"''"}>
                  {ex?.attachements?.map((attach) => (
                    <ListItem key={attach?.id}>
                      <Flex color="colorLink" align="center" gap={1}>
                        <IoLink />
                        <Link fontSize="14px" href={attach?.url} target="_blank">
                          {attach?.name}
                        </Link>
                        <Text color="black">- {attach?.created_at}</Text>
                      </Flex>
                    </ListItem>
                  ))}
                </UnorderedList>
              </Stack>
            )}
          </Stack>
        )}

        {watch('exemption') && ex && ex.show_nis_field && ex.type?.toLowerCase()?.includes('cadúnico') && (
          <Stack w={['100%', '100%', '100%', '50%', '50%']}>
            <FormControl display="flex" flexDir="row" alignItems="center">
              <Text fontWeight="bold" mr={2}>
                NIS:{' '}
              </Text>
              <Input {...register('nis')} type="text" />
            </FormControl>
            <MessageError>{formState.errors.nis?.message}</MessageError>
          </Stack>
        )}

        {watch('exemption') && ex && ex.show_redome_field && ex.type?.toLowerCase()?.includes('medula') && (
          <Stack w={['100%', '100%', '100%', '50%', '50%']}>
            <FormControl display="flex" flexDir="column">
              <Text fontWeight="bold" mr={2}>
                Código REDOME:{' '}
              </Text>
              <Input {...register('redome_code')} type="text" />
            </FormControl>
            <MessageError>{formState.errors.redome_code?.message}</MessageError>
          </Stack>
        )}

        {data?.resource?.has_observation && (
          <Stack w={['100%', '100%', '100%', '50%', '50%']}>
            <Text fontWeight="bold" mb={2}>
              Justificativa do recurso:
            </Text>
            <Textarea {...register('detail')} h="100px" />
            <MessageError>{formState.errors.detail?.message}</MessageError>
          </Stack>
        )}

        {data?.resource?.has_attachment && data?.max_attached > 0 && (
          <Stack>
            {/* <Button
              color="darkGrey"
              whiteSpace="break-spaces"
              w="fit-content"
              bg="white"
              border="1px solid darkGrey"
              boxShadow={'rgba(9, 30, 66, 0.25) 0px 4px 8px -2px, rgba(9, 30, 66, 0.08) 0px 0px 0px 1px;'}
              mt={4}
              onClick={() => setIsOpen((e) => !e)}
            >
              Enviar Arquivo
            </Button> */}

            <Stack w={['100%', '100%', '100%', '70%', '70%']}>
              <Text fontWeight="bold">Enviar anexos:</Text>
              <Text fontSize="sm" color="gray.600">
                Selecione todos os documentos que deseja enviar de uma só vez. Por exemplo, se forem 4, selecione os 4
                arquivos juntos antes de enviar.
              </Text>
              <Stack mt={4} borderRadius="4px" h="180px" bg="lightGrey" justify="center" p={5}>
                <FormControl display="flex" alignItems="center">
                  <FormLabel>Arquivo</FormLabel>
                  <Input multiple {...register('attachmentFile')} bg="white" type="file" alignContent="center" />
                </FormControl>
                <MessageError ml="4rem">{formState.errors.attachmentFile?.message}</MessageError>
                <Text cursor="pointer" onClick={clear} color="colorLink" ml="4rem">
                  Limpar
                </Text>
              </Stack>
            </Stack>
          </Stack>
        )}

        <Stack mb={10} flexDir="row" alignItems="center" justify="flex-end">
          <DefaultButton
            bg="lightGrey"
            color="black"
            onClick={() => {
              clear();
              // setIsOpen((e) => !e);
              navigate(-1);
            }}
            label="Cancelar"
          />

          <DefaultButton isDisabled={isPending} type="submit" label={isPending ? <Spinner /> : 'Enviar'} />
        </Stack>
      </Stack>
    </MainCard>
  );
};
export default NewResource;
